import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  BackNavigable,
  BackNavigationService,
  CommandStore,
  FilterAndSortComponent,
  HeaderComponent,
  Scan,
  ScanButtonComponent,
  ScannerStore,
  showRequestErrors,
  SOFTLINE_FEATURE_COMMANDS,
  SOFTLINE_FEATURE_SCANNER,
  StepHeaderComponent } from '@softline/application';
import { Store } from '@softline/core';
import { map, of, skip, Subscription, switchMap } from 'rxjs';
import { ArtikelHeaderComponent } from '../../../components/artikel-header/artikel-header.component';
import {
  SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
  SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_PALETTE } from '../../../kommissionierung.shared';
import { RemoteConfigService } from '../../../services/remote-config.service';
import { LlrBestellungStore } from '../../../store/bestellung.store';
import { LlrKommissionierungStore } from '../../../store/kommissionierung.store';
import { LlrPalleteStore } from '../../../store/palette.store';
import { Palette } from '../../../types/palette';

import { RepeatDirective } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
  selector: 'soft-palettendaten-erfassung',
  imports: [
    CommonModule,
    FilterAndSortComponent,
    HeaderComponent,
    StepHeaderComponent,
    ScanButtonComponent,
    RouterModule,
    ArtikelHeaderComponent,
    RepeatDirective,
    TranslatePipe],
  templateUrl: './palettenart-auswahl.component.html',
  styleUrls: ['./palettenart-auswahl.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush })
export class PalettenartAuswahlComponent implements OnInit, OnDestroy, BackNavigable {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly remoteConfigService = inject(RemoteConfigService);
  private readonly backNavigationService = inject(BackNavigationService);

  private bewegungSubscription?: Subscription;
  private scanSubscription?: Subscription;

  readonly paletteEinzelEingabe = this.remoteConfigService.config.paletteEinzelEingabe;

  readonly kommissionierung$ = this.store.observe(
    SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
    LlrKommissionierungStore.getters.data
  );

  readonly bewegung = toSignal(
    this.activatedRoute.paramMap.pipe(
      map((paramMap) => paramMap.get('id') ?? ''),
      switchMap((id) =>
        this.kommissionierung$.pipe(
          map((komm) => {
            const index = komm?.bewebskomms.findIndex((o) => o.id + '' === id);

            if (index < 0) return null;

            return komm?.bewebskomms[index];
          })
        )
      )
    )
  );

  readonly paletten = toSignal(
    this.store.observe(
      SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_PALETTE,
      LlrPalleteStore.getters.all
    )
  );

  readonly loading$ = this.store.observe(
    SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_PALETTE,
    LlrBestellungStore.getters.loading
  );


  async ngOnInit(): Promise<void> {
    this.backNavigationService.set(this);

    const paletten = this.paletten();

    this.store.commit(SOFTLINE_FEATURE_COMMANDS, CommandStore.mutations.addSet, {
      name: PalettenartAuswahlComponent,
      commands: [
        {
          name: '#LLR_KOMMISSIONIERUNG.AKTIONEN.LABEL_PALETTE_SCANNEN',
          class: 'menu action-menu action-menu-top',
          icon: 'fa-regular fa-barcode-scan',
          execute: async () => this.scanKommissionierungDaten() },
        {
          name: '#LLR_KOMMISSIONIERUNG.AKTIONEN.LABEL_PALETTE_ERSTELLEN',
          class: 'menu action-menu action-menu-top',
          icon: 'fa-regular fa-pallet',
          routerLink: `llr-bskomm/bewegung/${this.bewegung()?.id}/artikeldaten-erfassung` },
        {
          name: '#LLR_KOMMISSIONIERUNG.AKTIONEN.LABEL_VORHANDENE_PALETTE_AUSWAEHLEN',
          class: 'menu action-menu action-menu-top',
          icon: 'fa-regular fa-pallet-boxes',
          routerLink: `llr-bskomm/bewegung/${this.bewegung()?.id}/palettenauswahl`,
          canExecute: (paletten ? paletten.length : 0) <= 0 ? of(false) : of(true) }] });
    const bewe = this.bewegung();

    try {
      if (!bewe) return;

      await this.store.dispatch(
        SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_PALETTE,
        LlrPalleteStore.actions.loadMany,
        {
          clear: true,
          pathParams: {
            idkopfbskomm: bewe.idkopfbskomm,
            idbewebskomm: bewe.id } }
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }

    if (
      bewe?.kommissionierDaten?.palette?.id &&
      this.activatedRoute.snapshot.queryParamMap.get('paletteId')?.length === 0
    )
      await this.router.navigate(
        [`llr-bskomm/bewegung/${bewe.id}/artikeldaten-erfassung`],
        {
          queryParams: {
            paletteId: bewe?.kommissionierDaten?.palette?.id } }
      );

    this.scanSubscription = this.store
      .observe(SOFTLINE_FEATURE_SCANNER, ScannerStore.getters.latest)
      .pipe(skip(1))
      .subscribe(async (scan) => {
        await this.scanKommissionierungDaten(scan);
      });
  }

  async ngOnDestroy(): Promise<void> {
    if (this.bewegungSubscription && !this.bewegungSubscription.closed)
      this.bewegungSubscription.unsubscribe();

    if (this.scanSubscription && !this.scanSubscription.closed)
      this.scanSubscription.unsubscribe();

    this.backNavigationService.set(undefined);
    this.bewegungSubscription = undefined;
    this.scanSubscription = undefined;

    this.store.commit(
      SOFTLINE_FEATURE_COMMANDS,
      CommandStore.mutations.removeSet,
      PalettenartAuswahlComponent
    );
  }

  async navigateBack(): Promise<void> {
    try {
      const bewegung = this.bewegung();

      if (!bewegung) return;

      await this.store.dispatch(
        SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
        LlrKommissionierungStore.actions.artikelPositionAbbrechen,
        {
          idkopfbskomm: bewegung.idkopfbskomm + '',
          idbewebskomm: bewegung.id + '',
          bewe: bewegung }
      );
      await this.router.navigate(['llr-bskomm/artikelauswahl']);
      return;
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  private async scanKommissionierungDaten(scan?: Scan) {
    try {
      let scanParam;

      if (!scan) {
        scanParam = await this.store.dispatch(
          SOFTLINE_FEATURE_SCANNER,
          ScannerStore.actions.scan,
          {
            labelType: ['ean13', 'ean128', 'code39', 'code128', 'ean8'] }
        );
      } else {
        scanParam = scan;
      }

      if (!scanParam) return;

      const bewe = this.bewegung();

      if (!bewe) return;

      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
        LlrKommissionierungStore.actions.scanKommissionierungDaten,
        {
          idkopfbskomm: bewe.idkopfbskomm + '',
          idbewebskomm: bewe.id + '',
          type: scanParam.labelType ?? '',
          code: scanParam.data ?? '' }
      );

      if (!result) return;

      if (result.kommissionierDaten?.palette?.id)
        await this.router.navigate(
          [`llr-bskomm/bewegung/${result.id}/artikeldaten-erfassung`],
          {
            queryParams: {
              paletteId: result.kommissionierDaten.palette.id } }
        );

      return;
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  getPaletteArt(palette: Palette) {
    return palette?.palettearten[0];
  }
}
